import { useId } from "react";

import { t } from "../contract/strings";
import {
  LANE_W,
  LANE_X,
  PEOPLE,
  laneY,
  type LanesHeader,
  type SceneFrame,
} from "./welcome-scene";

/**
 * The drawing's own box: the prototype's 300-wide stage, cropped to what it draws. The
 * screen scales it uniformly into the space between the title and the controls
 * (`preserveAspectRatio` meet), so the table, the lanes and the notes card keep their
 * proportions on every phone and stay centred.
 */
export const STAGE_W = 300;
export const STAGE_H = 424;

const MONO = { fontFamily: "var(--font-mono)" } as const;
const UPPER = { textTransform: "uppercase" } as const;

/** The notes card's three lines: who said it, and its line of the sample notes. */
const NOTE_LINES = [
  { who: 0, line: "welcome.notes_line_1" },
  { who: 1, line: "welcome.notes_line_2" },
  { who: 2, line: "welcome.notes_line_3" },
] as const;

/** The card's header pill holds mono caps, so its width follows from its length. */
const PILL_CHAR_W = 8.5 * 0.6 + 0.4;

function lanesHeaderText(header: LanesHeader): string {
  switch (header.kind) {
    case "none":
      return "";
    case "in_meeting":
      return t("welcome.in_meeting");
    case "minutes":
      return t("welcome.meeting_minutes", { count: header.count });
    case "ended":
      return t("welcome.meeting_ended", { count: header.count });
  }
}

/** The scene at one moment, drawn from `sceneAt`. Decorative: the step's words say it. */
export function WelcomeStage({ frame }: { frame: SceneFrame }) {
  const uid = useId().replace(/:/g, "");
  const names = PEOPLE.map((p) => t(p.name));
  const ready = t("welcome.notes_ready", { count: PEOPLE.length });
  const pillW = ready.length * PILL_CHAR_W + 16;

  return (
    <svg
      className="welcome-scene absolute inset-0 size-full"
      viewBox={`0 0 ${STAGE_W} ${STAGE_H}`}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      style={{ fontFamily: "var(--font-body)" }}
    >
      <defs>
        <filter id={`${uid}b`} x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur stdDeviation="6" />
        </filter>
        <radialGradient id={`${uid}g`} cx="50%" cy="42%" r="65%">
          <stop offset="0%" stopColor="var(--surface)" />
          <stop offset="100%" stopColor="var(--surface-2)" />
        </radialGradient>
      </defs>

      <g opacity={frame.opacity}>
        {/* The table, a cup and a notebook. */}
        <ellipse cx={150} cy={167} rx={121} ry={87} fill="var(--ink)" opacity={0.07} />
        <ellipse cx={150} cy={158} rx={116} ry={84} fill={`url(#${uid}g)`} />
        <ellipse cx={150} cy={158} rx={104} ry={73} fill="none" stroke="var(--accent)" strokeOpacity={0.08} strokeWidth={1.5} />
        {/* A cup of coffee: a white rim with a light outline, and the coffee in it. */}
        <path d="M160 134 q7 4 0 9" stroke="var(--ink-soft)" strokeOpacity={0.35} strokeWidth={3} fill="none" strokeLinecap="round" />
        <circle cx={150} cy={138} r={10} fill="#fff" stroke="var(--ink-soft)" strokeOpacity={0.35} strokeWidth={1.5} />
        <circle cx={150} cy={138} r={6.5} fill="#7b4a2c" />
        <g transform="translate(224 168) rotate(14)">
          <rect
            x={-16}
            y={-12}
            width={32}
            height={24}
            rx={3}
            fill="var(--surface)"
            stroke="color-mix(in srgb, var(--accent) 14%, var(--bg))"
          />
          {[-5, 0, 5].map((y) => (
            <line
              key={y}
              x1={-10}
              x2={10}
              y1={y}
              y2={y}
              stroke="color-mix(in srgb, var(--accent) 8%, var(--bg))"
              strokeWidth={1.5}
            />
          ))}
          <line x1={-12} y1={17} x2={8} y2={21} stroke="var(--ink-soft)" strokeWidth={2} strokeLinecap="round" />
        </g>

        {/* Emma's open meeting breathes softly; a joining phone catches a burst of waves. */}
        {frame.beacon.map((b, n) => (
          <circle key={n} cx={b.cx} cy={b.cy} r={b.r} fill="none" stroke={PEOPLE[0].color} strokeWidth={1.2} opacity={b.opacity} />
        ))}
        {frame.waves.map((w, n) =>
          w.d ? (
            <path key={n} d={w.d} fill="none" stroke={PEOPLE[0].color} strokeWidth={2} strokeLinecap="round" opacity={w.opacity} />
          ) : null
        )}
        <line
          x1={frame.link.x1}
          y1={frame.link.y1}
          x2={frame.link.x2}
          y2={frame.link.y2}
          stroke={PEOPLE[0].color}
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeDasharray="1 5"
          opacity={frame.link.opacity}
        />

        {frame.people.map((o, i) => {
          const p = PEOPLE[i];
          return (
            <g key={p.name}>
              {o.ripples.map((c, n) => (
                <circle key={n} cx={p.ax} cy={p.ay} r={c.r} fill="none" stroke={p.color} strokeWidth={1.5} opacity={c.opacity} />
              ))}
              <circle cx={p.px} cy={p.py} r={22} fill={p.color} filter={`url(#${uid}b)`} opacity={o.glow} />
              <g transform={`translate(${o.x.toFixed(1)} ${o.y.toFixed(1)}) scale(${o.scale.toFixed(3)})`} opacity={o.opacity}>
                <rect x={-11} y={-18} width={22} height={36} rx={5} fill="var(--ink)" />
                <rect
                  x={-9}
                  y={-15}
                  width={18}
                  height={30}
                  rx={3}
                  fill={o.speaking ? p.color : o.inMeeting ? "var(--accent)" : "var(--ink-soft)"}
                  opacity={o.speaking ? 1 : o.inMeeting ? 0.55 : 0.35}
                />
                {o.bars.map((h, n) => (
                  <rect
                    key={n}
                    x={-6.4 + n * 3.4}
                    y={-h / 2}
                    width={2.2}
                    height={h}
                    rx={1}
                    fill="var(--bg)"
                    opacity={o.inMeeting ? 1 : 0}
                  />
                ))}
              </g>
              <g transform={`translate(${p.ax} ${p.ay})`}>
                <circle r={o.ring.r} fill="none" stroke={p.color} strokeWidth={2} opacity={o.ring.opacity} />
                <circle r={17} fill={p.color} />
                <text y={4.5} textAnchor="middle" fontSize={13} fontWeight={700} fill="#fff">
                  {names[i].charAt(0)}
                </text>
              </g>
              <text
                x={p.ax}
                y={p.ay < 150 ? p.ay - 26 : p.ay + 31}
                textAnchor="middle"
                fontSize={10}
                fontWeight={600}
                fill="var(--ink-soft)"
              >
                {names[i]}
              </text>
              {i > 0 ? <JoinedChip x={p.px} y={i === 2 ? p.py - 30 : p.py + 30} opacity={o.pill} /> : null}
            </g>
          );
        })}

        {/* Step 1: Emma taps Start. */}
        <circle cx={PEOPLE[0].px} cy={PEOPLE[0].py} r={frame.tap.r} fill="none" stroke={PEOPLE[0].color} strokeWidth={2} opacity={frame.tap.opacity} />
        {/* Step 4: each voice flows from its own phone into its own lane. */}
        {frame.dots.map((d, n) => (
          <circle key={n} cx={d.cx} cy={d.cy} r={2.6} fill={PEOPLE[d.who].color} opacity={d.opacity} />
        ))}

        {/* The lanes: who is in the meeting, and who spoke when. */}
        <g opacity={frame.lanes.opacity}>
          <text x={12} y={314} fontSize={8.5} fontWeight={700} fill="var(--ink-soft)" letterSpacing={0.8} style={{ ...MONO, ...UPPER }}>
            {lanesHeaderText(frame.lanes.header)}
          </text>
          {frame.people.map((o, i) => (
            <g key={i} opacity={o.lane}>
              <rect x={LANE_X} y={laneY(i)} width={LANE_W} height={14} rx={7} fill="var(--surface-2)" />
              <text x={12} y={laneY(i) + 10.5} fontSize={9.5} fontWeight={600} fill={PEOPLE[i].color}>
                {names[i]}
              </text>
            </g>
          ))}
          {frame.lanes.segs.map((s, n) => (
            <rect key={n} x={s.x} y={laneY(s.who)} width={s.width} height={14} rx={7} fill={PEOPLE[s.who].color} />
          ))}
          <line
            x1={frame.lanes.head.x}
            x2={frame.lanes.head.x}
            y1={318}
            y2={412}
            stroke="var(--ink)"
            strokeWidth={1.2}
            strokeDasharray="2 2"
            opacity={frame.lanes.head.opacity}
          />
        </g>

        {/* Step 5: the notes, every line with its speaker's name. */}
        <g opacity={frame.notes.opacity} transform={`translate(0 ${frame.notes.dy.toFixed(1)})`}>
          <rect
            x={14}
            y={304}
            width={272}
            height={114}
            rx={14}
            fill="var(--surface)"
            style={{ filter: "drop-shadow(0 8px 14px color-mix(in srgb, var(--ink) 13%, transparent))" }}
          />
          <text x={26} y={325} fontSize={10} fontWeight={700} fill="var(--ink)" letterSpacing={0.5} style={{ ...MONO, ...UPPER }}>
            {t("welcome.notes_title")}
          </text>
          <rect x={274 - pillW} y={313} width={pillW} height={16} rx={8} fill="var(--accent-soft)" />
          <text
            x={274 - pillW / 2}
            y={324}
            textAnchor="middle"
            fontSize={8.5}
            fontWeight={700}
            fill="var(--accent)"
            letterSpacing={0.4}
            style={{ ...MONO, ...UPPER }}
          >
            {ready}
          </text>
          {NOTE_LINES.map((l, n) => {
            const cy = 346 + n * 26;
            const v = frame.notes.lines[n];
            const p = PEOPLE[l.who];
            return (
              <g key={l.line} opacity={v.opacity} transform={`translate(0 ${v.dy.toFixed(1)})`}>
                <circle cx={35} cy={cy} r={9} fill={p.color} />
                <text x={35} y={cy + 3.2} textAnchor="middle" fontSize={9} fontWeight={700} fill="#fff">
                  {names[l.who].charAt(0)}
                </text>
                <text x={51} y={cy + 3.7} fontSize={10.5} fill="var(--ink)">
                  <tspan fontWeight={700} fill={p.color}>
                    {names[l.who]}
                  </tspan>{" "}
                  {t(l.line)}
                </text>
              </g>
            );
          })}
        </g>
      </g>
    </svg>
  );
}

/** The "Joined" chip beside a phone that has just joined: a tick and the word. */
function JoinedChip({ x, y, opacity }: { x: number; y: number; opacity: number }) {
  return (
    <g transform={`translate(${x} ${y})`} opacity={opacity}>
      <rect x={-34} y={-8.5} width={68} height={17} rx={8.5} fill="var(--ink)" />
      <path d="M-22 0.2 l2.6 2.6 l5 -5.2" fill="none" stroke="var(--bg)" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" />
      <text x={5} y={3.2} textAnchor="middle" fontSize={8.5} fontWeight={700} fill="var(--bg)" letterSpacing={0.4} style={{ ...MONO, ...UPPER }}>
        {t("welcome.joined")}
      </text>
    </g>
  );
}
